import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useDisclosure,
  HStack,
  VStack,
  Text,
  Badge,
  IconButton,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  CloseButton,
} from '@chakra-ui/react';
import { FaEdit, FaTrash, FaPlus, FaEye } from 'react-icons/fa';
import PageTable from 'components/layout/PageTable';
import rolePermissionService from 'services/rolePermissionService';
import authService from 'services/authService';
import RoleFormModal from './RoleFormModal';
import PermissionsModal from './PermissionsModal';

export default function RoleManagement() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const { isOpen: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure();
  const { isOpen: isPermissionsOpen, onOpen: onPermissionsOpen, onClose: onPermissionsClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const toast = useToast();
  const cancelRef = React.useRef();

  useEffect(() => {
    loadRoles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const response = await rolePermissionService.getRoles();
      setRoles(response.payload?.roles || []);
    } catch (error) {
      console.error('Error loading roles:', error);
      toast({
        title: 'Error',
        description: 'Error al cargar los roles',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = () => {
    setSelectedRole(null);
    setIsEditing(false);
    onFormOpen();
  };

  const handleEditRole = (role) => {
    setSelectedRole(role);
    setIsEditing(true);
    onFormOpen();
  };

  const handleViewPermissions = (role) => {
    setSelectedRole(role);
    onPermissionsOpen();
  };

  const handleDeleteRole = (role) => {
    setRoleToDelete(role);
    onDeleteOpen();
  };

  const confirmDelete = async () => {
    try {
      await rolePermissionService.deleteRole(roleToDelete.id);
      toast({
        title: 'Éxito',
        description: 'Rol eliminado correctamente',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      loadRoles();
    } catch (error) {
      console.error('Error deleting role:', error);
      toast({
        title: 'Error',
        description: 'Error al eliminar el rol',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      onDeleteClose();
      setRoleToDelete(null);
    }
  };

  const handleFormSuccess = () => {
    loadRoles();
    onFormClose();
    toast({
      title: 'Éxito',
      description: `Rol ${isEditing ? 'actualizado' : 'creado'} correctamente`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  // Verificar permisos
  const canCreateRoles = authService.hasPermission('roles.create');
  const canEditRoles = authService.hasPermission('roles.edit');
  const canDeleteRoles = authService.hasPermission('roles.delete');
  const canViewRoles = authService.hasPermission('roles.view');

  if (!canViewRoles) {
    return (
      <Box p={4}>
        <Text color="red.500">No tienes permisos para ver los roles</Text>
      </Box>
    );
  }

  return (
    <PageTable>
      <VStack spacing={4} align="stretch">
        <HStack justify="flex-end">
          {canCreateRoles && (
            <Button
              leftIcon={<FaPlus />}
              colorScheme="gray"
              onClick={handleCreateRole}
            >
              Nuevo Rol
            </Button>
          )}
        </HStack>

        <Box>
          {loading ? (
            <Text>Cargando roles...</Text>
          ) : (
            <PageTable.Scroll>
              <Table variant="simple">
                            <Thead>
                              <Tr>
                                <Th>Nombre</Th>
                                <Th>Descripción</Th>
                                <Th>Estado</Th>
                                <Th>Fecha Creación</Th>
                                <Th>Acciones</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {roles.map((role) => (
                                <Tr key={role.id}>
                                  <Td>
                                    <Text fontWeight="normal">{role.name}</Text>
                                  </Td>
                                  <Td>
                                    <Text fontSize="sm" color="gray.600">
                                      {role.description || 'Sin descripción'}
                                    </Text>
                                  </Td>
                                  <Td>
                                    <Badge 
                                      colorScheme={role.isActive ? 'green' : 'red'}
                                    >
                                      {role.isActive ? 'Activo' : 'Inactivo'}
                                    </Badge>
                                  </Td>
                                  <Td>
                                    <Text fontSize="sm">
                                      {new Date(role.createdAt).toLocaleDateString()}
                                    </Text>
                                  </Td>
                                  <Td>
                                    <HStack spacing={2}>
                                      <IconButton
                                        icon={<FaEye />}
                                        size="sm"
                                        colorScheme="gray"
                                        variant="ghost"
                                        onClick={() => handleViewPermissions(role)}
                                        aria-label="Ver permisos"
                                        title="Ver permisos"
                                      />
                                      {canEditRoles && (
                                        <IconButton
                                          icon={<FaEdit />}
                                          size="sm"
                                          colorScheme="gray"
                                          variant="ghost"
                                          onClick={() => handleEditRole(role)}
                                          aria-label="Editar rol"
                                          title="Editar rol"
                                        />
                                      )}
                                      {canDeleteRoles && role.name !== 'Admin' && (
                                        <IconButton
                                          icon={<FaTrash />}
                                          size="sm"
                                          colorScheme="red"
                                          variant="ghost"
                                          onClick={() => handleDeleteRole(role)}
                                          aria-label="Eliminar rol"
                                          title="Eliminar rol"
                                        />
                                      )}
                                    </HStack>
                                  </Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
            </PageTable.Scroll>
          )}
        </Box>
      </VStack>

      {/* Modal de formulario */}
      <RoleFormModal
        isOpen={isFormOpen}
        onClose={onFormClose}
        role={selectedRole}
        isEditing={isEditing}
        onSuccess={handleFormSuccess}
      />

      {/* Modal de permisos */}
      <PermissionsModal
        isOpen={isPermissionsOpen}
        onClose={onPermissionsClose}
        role={selectedRole}
      />

      {/* Diálogo de confirmación de eliminación */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
        closeOnOverlayClick={false}
      >
        <AlertDialogOverlay>
          <AlertDialogContent position="relative">
            <CloseButton position="absolute" right={2} top={2} onClick={onDeleteClose} />
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirmar Eliminación
            </AlertDialogHeader>
            <AlertDialogBody>
              ¿Estás seguro de que deseas eliminar el rol "{roleToDelete?.name}"? 
              Esta acción no se puede deshacer.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={confirmDelete} ml={3}>
                Eliminar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </PageTable>
  );
}